/* eslint-disable no-underscore-dangle */
import { useCallback, useContext, useEffect } from "react";

import { setUser as setSentryUser } from "@sentry/react";
import axios from "axios";
import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router-dom";

import { inLocalDevEnvironment } from "utils";

import {
  clearClientData,
  extendSessionTTL,
  getLocalAuth,
  getLocalRefreshToken,
  getLocalUser,
  localStorageDataIsStale,
  setLocalAccountLink,
  setUser,
  storeSessionInitData,
} from "../../clientData/clientDataRepo";
import { AUTH_ONLY_RELATIVE_URLS, ClientAppEvent } from "../../constants";
import { authContext } from "../../context";

import type { AxiosRequestConfig } from "axios";

import type { ActivationFlow } from "../../constants";

export interface LoginParams {
  email: string;
  password: string;
  voluntellLogin?: boolean;
  shipmentId?: string | null;
  flow?: ActivationFlow | null;
}

export function useAuthContext() {
  const userCtx = useContext(authContext);
  const navigate = useNavigate();
  const posthog = usePostHog();

  const signout = useCallback(() => {
    // Reset clears the identified person and ALL group associations (COMPANY,
    // BRANCH) so a subsequent user on this browser is not attributed to the
    // previous user's company. Runs before the redirect; reset persists synchronously.
    posthog?.reset();
    clearClientData();
    window.location.href = "/";
  }, [posthog]);

  // update to only proceed if TTL makes sense
  const refreshTokens = useCallback(() => {
    const localRefreshToken = getLocalRefreshToken();
    const localUser = getLocalUser();

    if (
      localRefreshToken === null ||
      localUser === null ||
      localStorageDataIsStale()
    ) {
      signout();
      return;
    }

    axios
      .patch(`${import.meta.env.REACT_APP_API_URL}/tokens/refresh`, {
        email: localUser.email,
        refresh_token: localRefreshToken,
      })
      .then((res) => {
        const {
          data: { data },
        } = res;
        // no refreshToken is sent back on response: refreshing a token provides access token,
        // not a refresh token
        const accountLink = data.account_link ?? null;
        setLocalAccountLink(accountLink);
        userCtx.dispatch({
          type: "refreshToken",
          data: {
            auth: data.auth,
            user: localUser,
            pubnub: data.pubnub,
            accountLink,
          },
        });
        extendSessionTTL();
        const currentPath = window.location.pathname;
        const redirectTo =
          currentPath === "/login" || currentPath === "/mfa-challenge"
            ? "/"
            : currentPath + window.location.search + window.location.hash;
        navigate(redirectTo);
      })
      .catch(() => {
        // refresh token has likely expired
        signout();
      });
  }, [userCtx, navigate, signout]);

  function identifyUserInSentry(user: User) {
    if (!inLocalDevEnvironment()) {
      setSentryUser({ email: user.email, id: user.id });
    }
  }

  function identifyUserInPostHog(user: User) {
    if (posthog?.identify && user.email) {
      const shipperRole = user.roles?.find((role) => role.type === "SHIPPER");
      const carrierRole = user.roles?.find((role) => role.type === "CARRIER");
      const branchAdminRole = user.roles?.find(
        (role) => role.type === "BRANCH_ADMINISTRATOR",
      );
      const adminRole = user.roles?.find(
        (role) => role.type === "ADMINISTRATOR",
      );

      const roleWithBranch = user.roles?.find((role) => role.branch !== null);

      const primaryRole = shipperRole ?? carrierRole;

      posthog.identify(user.email, {
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        user_id: user.id,
        is_company_admin: adminRole !== undefined,
        is_branch_admin: branchAdminRole !== undefined,
        primary_role_type: primaryRole?.type,
        primary_role_id: primaryRole?.id,
        company_id: user.roles?.[0]?.company?.id,
        company_name: user.roles?.[0]?.company?.name,
        company_type: user.roles?.[0]?.company?.company_type,
        shipper_role: shipperRole,
        carrier_role: carrierRole,
        branch_admin_role: branchAdminRole,
        admin_role: adminRole,
      });
      if (user.roles?.[0]?.company) {
        const { company } = user.roles[0];
        posthog?.group("COMPANY", company.id.toString(), {
          company_type: company.company_type,
          name: company.name,
          company_id: company.id,
          is_external_carrier: company.is_external_carrier ?? false,
        });
      }

      if (roleWithBranch?.branch) {
        const { branch } = roleWithBranch;
        posthog?.group("BRANCH", branch.toString());
      }

      // to save session recording budget, do not record carrier sessions 90% of the time
      if (primaryRole?.type === "CARRIER" && Math.random() < 0.9) {
        posthog?.stopSessionRecording();
      }

      posthog?.capture(ClientAppEvent.USER_LOGGED_IN, {
        user_id: user.id,
        email: user.email,
      });
    }
  }

  function onLoginSuccess(
    user: User,
    auth: SignInAuthData,
    pubnub: PubnubClientData,
    accountLink: AccountLink | null,
    redirectPath?: string,
  ) {
    userCtx.dispatch({
      type: "login",
      data: {
        user,
        auth,
        pubnub,
        accountLink,
      },
    });
    storeSessionInitData(user, auth, accountLink);
    identifyUserInSentry(user);
    identifyUserInPostHog(user);
    if (!inLocalDevEnvironment()) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const _cio = (window as any)._cio ?? [];
      if (_cio?.track) {
        _cio.track("Unsuppress Person", { userId: user.cognito_id });
      }
    }
    // Auth-only URLs (rendered only by the unauthenticated Switcher) have no
    // match in the authenticated route tree, so navigating to them would render
    // nothing before useLoadPath redirects — send them to '/' instead.
    const targetPath =
      redirectPath ??
      (AUTH_ONLY_RELATIVE_URLS.includes(window.location.pathname)
        ? "/"
        : window.location.pathname +
          window.location.search +
          window.location.hash);
    navigate(targetPath);
  }

  function handleLoginError(errorStatus: number, message?: string) {
    // Login 401s are opaque (body must not leak why), so the client-side event
    // records only the HTTP status — enough to find the failed-login session
    // replay in PostHog. The failure reason is tracked server-side (backend
    // login_failed event), where it can be known without leaking to the client.
    posthog?.capture(ClientAppEvent.LOGIN_ERROR, {
      http_status: errorStatus,
    });
    let loginError =
      "There was an error while signing in. Please contact us if the issue persists.";
    if (message) {
      loginError = message;
    } else if (errorStatus === 401) {
      loginError = "Failed to log in. Please contact us.";
    }
    userCtx.dispatch({
      type: "loginError",
      data: { loginErrorMsg: loginError },
    });
  }

  const refresh = useCallback(() => {
    const localUser = getLocalUser();
    const localRefreshToken = getLocalRefreshToken();
    if (localUser != null && localRefreshToken != null) {
      refreshTokens();
    } else {
      signout();
    }
  }, [refreshTokens, signout]);

  const login = async (params: LoginParams) => {
    const {
      email,
      password,
      voluntellLogin = false,
      shipmentId = null,
      flow = null,
    } = params;
    clearClientData();
    userCtx.dispatch({ type: "requestLogin", data: { voluntellLogin } });
    return axios
      .post(`${import.meta.env.REACT_APP_API_URL}/login`, {
        email,
        password,
        is_voluntell_login: voluntellLogin,
        shipment_id: shipmentId,
        flow,
      })
      .then((res) => {
        const {
          data: { data },
        } = res;
        const accountLink = data.account_link ?? null;
        if (data.challenge === "SMS_MFA") {
          userCtx.dispatch({
            type: "mfaChallenge",
            data: {
              mfaChallenge: {
                challenge: data.challenge,
                session: data.session,
                destination: data.destination,
                email,
              },
            },
          });
          navigate("/mfa-challenge");
        } else if (data.auth.ChallengeName === "NEW_PASSWORD_REQUIRED") {
          userCtx.dispatch({
            type: "changePasswordChallenge",
            data: {
              user: data.user,
              challengeAuth: data.auth,
              isVoluntell: voluntellLogin,
              pubnub: data.pubnub,
              accountLink,
            },
          });
          if (voluntellLogin) {
            // track activation link click (user hasn't completed activation yet)
            if (posthog && data) {
              const shipperRole = data.user.roles?.find(
                (r: Role) => r.type === "SHIPPER",
              );
              const roleType = shipperRole ? "SHIPPER" : "CARRIER";
              posthog?.capture(
                ClientAppEvent.VOLUNTELL_ACCOUNT_ACTIVATION_LINK_HIT,
                {
                  user_id: data.user.id,
                  email: data.user.email,
                  shipment_id: shipmentId,
                  roleType,
                },
              );
            }
            const activationParams = new URLSearchParams({ activate: "1" });
            if (shipmentId) activationParams.set("shipment", shipmentId);
            if (flow) activationParams.set("flow", flow);
            navigate(`/force-change-password?${activationParams.toString()}`);
          } else {
            navigate("/force-change-password");
          }
        } else {
          onLoginSuccess(data.user, data.auth, data.pubnub, accountLink);
        }
      })
      .catch((e) => {
        handleLoginError(e?.response?.status, e?.response?.data?.message);
      });
  };

  const refreshChatTokens = useCallback(
    (onRefreshSuccess?: () => void) => {
      if (userCtx.user && userCtx.auth?.AuthenticationResult.IdToken) {
        axios
          .get(`${import.meta.env.REACT_APP_API_URL}/user/chat-session`, {
            headers: {
              Authorization: `Bearer ${userCtx.auth?.AuthenticationResult.IdToken}`,
            },
          })
          .then((res) => {
            userCtx.dispatch({
              type: "updatePubnubData",
              data: { pubnub: res.data.pubnub },
            });
          })
          .then(() => {
            if (onRefreshSuccess) {
              onRefreshSuccess();
            }
          });
      }
      return null;
    },
    [userCtx],
  );

  async function forceChangePassword(
    email: string,
    newPassword: string,
    session: string,
    shipmentId: string | null = null,
  ) {
    return axios
      .post(`${import.meta.env.REACT_APP_API_URL}/challenge/change-password`, {
        email,
        new_password: newPassword,
        session,
      })
      .then((res) => {
        const {
          data: { data },
        } = res;
        const accountLink = data.account_link ?? null;
        if (data.challenge === "SMS_MFA") {
          userCtx.dispatch({
            type: "mfaChallenge",
            data: {
              mfaChallenge: {
                challenge: data.challenge,
                session: data.session,
                destination: data.destination,
                email,
                redirectPath: shipmentId
                  ? `/shipments/${shipmentId}?focus=quotes`
                  : "/",
              },
            },
          });
          navigate("/mfa-challenge");
        } else {
          const redirectPath = shipmentId
            ? `/shipments/${shipmentId}?focus=quotes`
            : "/";
          onLoginSuccess(
            data.user,
            data.auth,
            data.pubnub,
            accountLink,
            redirectPath,
          );
        }
      })
      .catch((e) => {
        const status = e?.response?.status;
        let message;
        if (status === 401) {
          message =
            "Your activation session has expired. Please use the link from your email to try again, or contact us for assistance.";
        }
        handleLoginError(status, message);
      });
  }

  const setLoginError = (errorMsg: string) => {
    userCtx.dispatch({ type: "loginError", data: { loginErrorMsg: errorMsg } });
  };

  const verifyMFA = async (code: string) => {
    const { mfaChallenge } = userCtx;
    if (!mfaChallenge) return undefined;
    userCtx.dispatch({ type: "requestLogin", data: { voluntellLogin: false } });
    return axios
      .post(`${import.meta.env.REACT_APP_API_URL}/mfa/challenge`, {
        email: mfaChallenge.email,
        code,
        session: mfaChallenge.session,
      })
      .then((res) => {
        const {
          data: { data },
        } = res;
        const accountLink = data.account_link ?? null;
        onLoginSuccess(
          data.user,
          data.auth,
          data.pubnub,
          accountLink,
          mfaChallenge.redirectPath,
        );
      })
      .catch((e) => {
        const newSession = e?.response?.data?.session;
        if (newSession && mfaChallenge) {
          userCtx.dispatch({
            type: "mfaChallenge",
            data: {
              mfaChallenge: {
                ...mfaChallenge,
                session: newSession,
              },
            },
          });
        }
        const message =
          e?.response?.data?.message ??
          "Verification failed. Please try again.";
        setLoginError(message);
      });
  };

  const clearMfaChallenge = () => {
    userCtx.dispatch({ type: "clearMfaChallenge" });
  };

  const refreshUser = useCallback(async () => {
    const { auth, dispatch } = userCtx;
    if (auth == null) {
      signout();
      return;
    }

    const idToken = auth.AuthenticationResult.IdToken;
    const config: AxiosRequestConfig = {
      method: "get",
      url: `${import.meta.env.REACT_APP_API_URL}/user`,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    };
    try {
      const res = await axios.request(config);
      setUser(res.data as User);
      dispatch({ type: "setUser", data: { user: res.data } });
    } catch {
      signout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signout, userCtx.auth, userCtx.dispatch]);

  async function signup(
    params: SignUpPayload,
    password: string,
    role: RoleValue,
  ) {
    const {
      email,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      first_name,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      last_name,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      recatpcha_token,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      work_phone_number,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      work_phone_number_extension,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cell_phone_number,
    } = params;

    return axios
      .post(`${import.meta.env.REACT_APP_API_URL}/signup`, {
        email,
        password,
        first_name,
        last_name,
        recatpcha_token,
        work_phone_number,
        work_phone_number_extension,
        cell_phone_number,
        role,
      })
      .then((res) => {
        const {
          data: { data },
        } = res;
        userCtx.dispatch({ type: "setUser", data: { user: data.user } });
      });
  }

  useEffect(() => {
    const localStorageAuth = getLocalAuth();
    const localStorageUser = getLocalUser();

    if (
      userCtx.auth == null &&
      userCtx.user == null &&
      localStorageAuth != null &&
      localStorageUser != null &&
      !userCtx.isInitialized
    ) {
      /*
        if local storage contains all the info we need, and
        the auth context is not initialized, then
        refresh the access token and the user data.
        (So that auth data can be loaded in memory/into the auth context).
        This hook runs on every page load that actually refreshes the page
        (such as a window.location.reload();)
      */
      refreshTokens();
    } else if (
      (localStorageAuth == null || localStorageUser == null) &&
      userCtx.isLoading
    ) {
      // local storage does not contain the info required to refresh session: sign out
      userCtx.dispatch({ type: "requireAuth" });
    }
  }, [userCtx, refreshTokens, signout]);

  let idToken = null;
  if (userCtx.auth != null) {
    idToken = userCtx.auth.AuthenticationResult.IdToken;
  }

  const accessToken = userCtx.auth?.AuthenticationResult?.AccessToken ?? null;

  return {
    user: userCtx.user,
    isAuthenticated: userCtx.isAuthenticated,
    challenge: userCtx.challengeAuth,
    isLoading: userCtx.isLoading,
    isInitialized: userCtx.isInitialized,
    loginErrorMsg: userCtx.loginError,
    attemptedVoluntellLogin: userCtx.attemptedVoluntellLogin,
    login,
    signup,
    signout,
    refreshTokens: refresh,
    refreshUser,
    setLoginError,
    forceChangePassword,
    idToken,
    pubnub: userCtx.pubnub,
    refreshChatTokens,
    accountLink: userCtx.accountLink,
    accessToken,
    mfaChallenge: userCtx.mfaChallenge,
    verifyMFA,
    clearMfaChallenge,
  };
}
